import React, { useState } from "react";
import { createPortal } from "react-dom";
import { attendanceViewApi, errorMessage } from "../adminApi";
import { useAdminToast } from "./Toast";
import { Button, Modal } from "./ui";
import { Icon } from "./icons";
import { hasRole } from "../../context/AuthContext";
import { formatDate, formatTime, attendanceStatusLabel } from "../format";

/**
 * HAPUS ABSENSI (dari modal detail di Monitoring Absensi & Monitor Anomali).
 * ============================================================================
 * Untuk membersihkan absen bermasalah, mis. karyawan tak sengaja check-in lalu
 * langsung check-out. Hanya SUPER_ADMIN / HR_ADMIN (backend juga menolak role lain).
 *
 * Yang terjadi saat dihapus:
 *  - Baris absensi di PLATFORM dihapus permanen (log mentah `attendance_raw` tetap
 *    ada; penghapusan tercatat di Audit Log berikut snapshot datanya).
 *  - Data di ODOO TIDAK ikut terhapus — HRD mengoreksinya manual di Odoo. Karena
 *    itu dialog konfirmasi menyebut ID Odoo-nya bila absen ini sudah tersinkron.
 * ============================================================================
 */

export const canDeleteAttendance = (user) => hasRole(user, "SUPER_ADMIN", "HR_ADMIN");

function Row({ label, value }) {
  return (
    <div className="flex justify-between gap-3 text-sm">
      <span className="text-slate-400">{label}</span>
      <span className="font-semibold text-slate-800 text-right">{value ?? "-"}</span>
    </div>
  );
}

/**
 * @param {{record: object, onDeleted?: (record: object) => void}} props
 *  record = data absensi dari API view (butuh: id, employee_name/username, date,
 *  check_in, check_out, status, source, odoo_attendance_id, synced_to_odoo).
 */
export default function DeleteAttendanceAction({ record, onDeleted }) {
  const toast = useAdminToast();
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  if (!record?.id) return null;

  const inOdoo = record.odoo_attendance_id != null || record.synced_to_odoo === true;

  const confirmDelete = async () => {
    setBusy(true);
    let res = null;
    let failed = false;
    try {
      res = await attendanceViewApi.remove(record.id);
    } catch (e) {
      failed = true;
      toast.error(errorMessage(e));
    }
    setBusy(false);
    if (failed) return;
    setConfirming(false);
    toast.success(
      res?.odoo_attendance_id
        ? `Absensi dihapus. Ingat: koreksi manual di Odoo (ID ${res.odoo_attendance_id}).`
        : "Absensi berhasil dihapus."
    );
    onDeleted?.(record);
  };

  return (
    <>
      <Button variant="ghost" size="sm" className="text-rose-600 hover:bg-rose-50 hover:text-rose-700" onClick={() => setConfirming(true)}>
        <Icon.Trash size={16} /> Hapus Absensi
      </Button>

      {/* Portal ke <body>: dialog ini muncul DI ATAS modal detail dan tak ikut ter-scroll/terpotong olehnya. */}
      {createPortal(
        <Modal
          open={confirming}
          onClose={() => !busy && setConfirming(false)}
          title="Hapus absensi ini?"
          footer={
            <>
              <Button variant="secondary" onClick={() => setConfirming(false)} disabled={busy}>
                Batal
              </Button>
              <Button variant="danger" onClick={confirmDelete} disabled={busy}>
                {busy ? "Menghapus…" : "Ya, Hapus"}
              </Button>
            </>
          }
        >
          <p className="text-sm text-slate-700">Apakah Anda yakin ingin menghapus absensi ini?</p>

          <div className="mt-3 space-y-1.5 rounded-xl border border-slate-200 bg-slate-50 p-3">
            <Row label="Karyawan" value={record.employee_name || record.username} />
            <Row label="Tanggal" value={formatDate(record.date)} />
            <Row label="Check-in" value={formatTime(record.check_in)} />
            <Row label="Check-out" value={formatTime(record.check_out)} />
            <Row label="Status" value={attendanceStatusLabel(record.status)} />
          </div>

          {inOdoo ? (
            <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              <b>Sudah tercatat di Odoo</b>
              {record.odoo_attendance_id != null ? ` (ID ${record.odoo_attendance_id})` : ""}. Menghapus di sini{" "}
              <b>tidak menghapus data di Odoo</b> — koreksi/hapus manual di Odoo.
            </div>
          ) : (
            <div className="mt-3 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-500">
              Absensi ini belum tersinkron ke Odoo.
            </div>
          )}

          <p className="mt-3 text-xs text-rose-600">
            Tindakan ini tidak dapat dibatalkan. Penghapusan dicatat di Audit Log.
            {record.source === "MOBILE" ? " Karyawan dapat absen ulang di hari yang sama." : ""}
          </p>
        </Modal>,
        document.body
      )}
    </>
  );
}
